/**
 * Map layer component that renders waterway LineString features
 * and a highlight layer for the currently selected waterway.
 */
import { useEffect, useRef } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { useMap } from '../../components/ui/map';
import {
  applyWaterwayClickSelection,
  isAdditiveSelectionGesture,
  type ModifierEventLike,
} from '../../lib/map-editor-interactions';

interface WaterwayLineLayerProps {
  waterwayGeoJson: GeoJSON.FeatureCollection;
  highlightGeoJson: GeoJSON.FeatureCollection;
  selectedWaterwayIds: string[];
  isAdmin: boolean;
  onSelectedWaterwayIdsChange: (ids: string[]) => void;
}

type PixelPoint = { x: number; y: number };

function pointDistanceSq(a: PixelPoint, b: PixelPoint): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function pointToSegmentDistanceSq(p: PixelPoint, a: PixelPoint, b: PixelPoint): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const lenSq = vx * vx + vy * vy;
  if (lenSq <= 0) return pointDistanceSq(p, a);

  const t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / lenSq;
  if (t <= 0) return pointDistanceSq(p, a);
  if (t >= 1) return pointDistanceSq(p, b);

  return pointDistanceSq(p, { x: a.x + t * vx, y: a.y + t * vy });
}

function distanceToFeatureLineSq(
  feature: GeoJSON.Feature,
  clickPoint: PixelPoint,
  project: (lngLat: [number, number]) => PixelPoint,
): number {
  const geometry = feature.geometry as GeoJSON.LineString | null;
  const coords = geometry?.coordinates ?? [];
  if (coords.length === 0) return Number.POSITIVE_INFINITY;

  const projected = coords
    .map(([lng, lat]) => project([Number(lng), Number(lat)]))
    .filter((pt) => Number.isFinite(pt.x) && Number.isFinite(pt.y));

  if (projected.length === 0) return Number.POSITIVE_INFINITY;
  if (projected.length === 1) return pointDistanceSq(clickPoint, projected[0]);

  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < projected.length - 1; i += 1) {
    const distSq = pointToSegmentDistanceSq(clickPoint, projected[i], projected[i + 1]);
    if (distSq < best) best = distSq;
  }
  return best;
}

export function isWaterwayAdditiveSelectionGesture(
  originalEvent: ModifierEventLike,
  shiftPressedFallback: boolean,
): boolean {
  return isAdditiveSelectionGesture(originalEvent, shiftPressedFallback);
}

export function pickWaterwayIdFromHits(
  hits: GeoJSON.Feature[],
  clickPoint: PixelPoint,
  project: (lngLat: [number, number]) => PixelPoint,
  selectedWaterwayIds: Set<string>,
  preferUnselected: boolean,
): string | null {
  const bestByWaterwayId = new Map<string, {
    distanceSq: number;
    isCanalplan: boolean;
    hasCanalplanLeaf: boolean;
  }>();

  for (const feature of hits) {
    const waterwayId = typeof feature.properties?.waterwayId === 'string'
      ? feature.properties.waterwayId
      : null;
    if (!waterwayId) continue;
    if (feature.geometry?.type !== 'LineString') continue;

    const distanceSq = distanceToFeatureLineSq(feature, clickPoint, project);
    const source = typeof feature.properties?.source === 'string'
      ? feature.properties.source.trim().toLowerCase()
      : null;
    const isCanalplan = source === 'canalplan';
    const isCanalplanLeaf = feature.properties?.canalplanIsLeaf === true;

    const current = bestByWaterwayId.get(waterwayId);
    if (!current || distanceSq < current.distanceSq) {
      bestByWaterwayId.set(waterwayId, {
        distanceSq,
        isCanalplan,
        hasCanalplanLeaf: isCanalplan && isCanalplanLeaf,
      });
      continue;
    }

    if (distanceSq === current.distanceSq) {
      bestByWaterwayId.set(waterwayId, {
        distanceSq,
        isCanalplan: current.isCanalplan || isCanalplan,
        hasCanalplanLeaf: current.hasCanalplanLeaf || (isCanalplan && isCanalplanLeaf),
      });
      continue;
    }

    bestByWaterwayId.set(waterwayId, {
      distanceSq: current.distanceSq,
      isCanalplan: current.isCanalplan || isCanalplan,
      hasCanalplanLeaf: current.hasCanalplanLeaf || (isCanalplan && isCanalplanLeaf),
    });
  }

  const scored = Array.from(bestByWaterwayId.entries()).map(([id, stats]) => ({
    id,
    distanceSq: stats.distanceSq,
    isCanalplan: stats.isCanalplan,
    hasCanalplanLeaf: stats.hasCanalplanLeaf,
  }));
  if (scored.length === 0) return null;

  // CanalPlan geometries can overlap parent/child rows. When a leaf row is
  // present under the click, ignore non-leaf CanalPlan candidates so admins
  // can select the individual waterway instead of its parent aggregate.
  const hasCanalplanLeaf = scored.some((candidate) => candidate.isCanalplan && candidate.hasCanalplanLeaf);
  const activeCandidates = hasCanalplanLeaf
    ? scored.filter((candidate) => !candidate.isCanalplan || candidate.hasCanalplanLeaf)
    : scored;
  activeCandidates.sort((a, b) => a.distanceSq - b.distanceSq);

  if (preferUnselected) {
    const unselected = activeCandidates.find((candidate) => !selectedWaterwayIds.has(candidate.id));
    if (unselected) return unselected.id;
  }

  return activeCandidates[0].id;
}

export function WaterwayLineLayer({
  waterwayGeoJson,
  highlightGeoJson,
  selectedWaterwayIds,
  isAdmin,
  onSelectedWaterwayIdsChange,
}: WaterwayLineLayerProps) {
  const { map, isLoaded } = useMap();
  const selectedWaterwayIdsRef = useRef<string[]>(selectedWaterwayIds);
  const shiftPressedRef = useRef(false);

  useEffect(() => {
    selectedWaterwayIdsRef.current = selectedWaterwayIds;
  }, [selectedWaterwayIds]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') shiftPressedRef.current = true;
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') shiftPressedRef.current = false;
    };
    const onBlur = () => {
      shiftPressedRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  useEffect(() => {
    if (!map || !isLoaded) return;
    if (isAdmin) {
      map.boxZoom.disable();
      return () => {
        map.boxZoom.enable();
      };
    }
  }, [map, isLoaded, isAdmin]);

  useEffect(() => {
    if (!map || !isLoaded) return;

    const sourceId = 'waterways-source';
    const layerId = 'waterways-layer';

    const addLineLayer = () => {
      try {
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, { type: 'geojson', data: waterwayGeoJson as any });
        } else {
          (map.getSource(sourceId) as GeoJSONSource).setData(waterwayGeoJson as any);
        }

        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': [
                'match', ['get', 'waterwayType'],
                'canal', '#3b82f6',
                'river', '#0ea5e9',
                '#3b82f6',
              ] as any,
              'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.5, 8, 2.5, 10, 3, 14, 5],
              'line-opacity': 0.85,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }
      } catch (err) {
        console.error('[WaterwayLineLayer] addLineLayer error:', err);
      }
    };

    addLineLayer();

    // Highlight layer
    const highlightSourceId = 'waterways-highlight-source';
    const highlightLayerId = 'waterways-highlight-layer';

    if (!map.getSource(highlightSourceId)) {
      map.addSource(highlightSourceId, { type: 'geojson', data: highlightGeoJson as any });
    }

    if (!map.getLayer(highlightLayerId)) {
      map.addLayer({
        id: highlightLayerId,
        type: 'line',
        source: highlightSourceId,
        paint: {
          'line-color': '#f59e0b',
          'line-width': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 6, 14, 9],
          'line-opacity': 0.95,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      });
    }

    // Click handler for waterway selection (admin only)
    const handleWaterwayClick = (e: any) => {
      if (!isAdmin) return;
      const additiveHeld = isWaterwayAdditiveSelectionGesture(
        e.originalEvent as ModifierEventLike,
        shiftPressedRef.current,
      );
      const tolerance = 12;
      const bbox: [[number, number], [number, number]] = [
        [e.point.x - tolerance, e.point.y - tolerance],
        [e.point.x + tolerance, e.point.y + tolerance],
      ];
      const features = map.queryRenderedFeatures(bbox, { layers: [layerId] }) as unknown as GeoJSON.Feature[];
      if (features.length === 0) {
        if (!additiveHeld) onSelectedWaterwayIdsChange([]);
        return;
      }

      const pickedWaterwayId = pickWaterwayIdFromHits(
        features,
        e.point as PixelPoint,
        (lngLat) => map.project(lngLat as [number, number]),
        new Set(selectedWaterwayIdsRef.current),
        additiveHeld,
      );
      if (!pickedWaterwayId) {
        if (!additiveHeld) onSelectedWaterwayIdsChange([]);
        return;
      }

      const nextSelection = applyWaterwayClickSelection(
        selectedWaterwayIdsRef.current,
        pickedWaterwayId,
        additiveHeld,
      );
      onSelectedWaterwayIdsChange(nextSelection);
    };

    map.on('click', handleWaterwayClick);

    // Cursor hint for admin
    let cursorSet = false;
    const handleMouseMove = isAdmin
      ? (e: any) => {
          const tolerance = 8;
          const bbox: [[number, number], [number, number]] = [
            [e.point.x - tolerance, e.point.y - tolerance],
            [e.point.x + tolerance, e.point.y + tolerance],
          ];
          const features = map.queryRenderedFeatures(bbox, { layers: [layerId] });
          if (features.length > 0) {
            if (!cursorSet) { map.getCanvas().style.cursor = 'pointer'; cursorSet = true; }
          } else if (cursorSet) {
            map.getCanvas().style.cursor = '';
            cursorSet = false;
          }
        }
      : null;

    if (handleMouseMove) map.on('mousemove', handleMouseMove);

    // Re-add after style changes
    const onStyleData = () => {
      try {
        if (!map.getSource(sourceId)) addLineLayer();
        if (!map.getSource(highlightSourceId)) {
          map.addSource(highlightSourceId, { type: 'geojson', data: highlightGeoJson as any });
        }
        if (!map.getLayer(highlightLayerId)) {
          map.addLayer({
            id: highlightLayerId,
            type: 'line',
            source: highlightSourceId,
            paint: {
              'line-color': '#f59e0b',
              'line-width': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 6, 14, 9],
              'line-opacity': 0.95,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }
      } catch (err) {
        console.error('[WaterwayLineLayer] styledata error:', err);
      }
    };
    map.on('styledata', onStyleData);

    return () => {
      map.off('click', handleWaterwayClick);
      if (handleMouseMove) map.off('mousemove', handleMouseMove);
      map.off('styledata', onStyleData);
      try {
        if (map.getLayer(highlightLayerId)) map.removeLayer(highlightLayerId);
        if (map.getSource(highlightSourceId)) map.removeSource(highlightSourceId);
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch { /* Map may already be removed */ }
    };
  }, [
    isLoaded,
    map,
    waterwayGeoJson,
    isAdmin,
    highlightGeoJson,
    onSelectedWaterwayIdsChange,
  ]);

  // Keep source data in sync
  useEffect(() => {
    if (!map || !isLoaded) return;
    try {
      const src = map.getSource('waterways-source') as GeoJSONSource | undefined;
      src?.setData(waterwayGeoJson as any);
      const hlSrc = map.getSource('waterways-highlight-source') as GeoJSONSource | undefined;
      hlSrc?.setData(highlightGeoJson as any);
    } catch { /* Source may not exist yet */ }
  }, [isLoaded, map, waterwayGeoJson, highlightGeoJson]);

  return null;
}
